import { z } from "zod";
import {
	BLOCK_INTERACTION_HOLD_MS,
	estimateTextMs,
} from "../../timeline/reading-rate";
import {
	asRecord,
	blockProvenanceHintSchema,
	compact,
	emptyBlockSettingsSchema,
	joinParts,
	optionalStr,
	pickProvenance,
	str,
	TOPIC_BLOCK_INSTRUCTION_TEMPLATES,
} from "../content-helpers";
import {
	ALL_BLOCK_PATCH_OPS,
	defineBlockKind,
	STRUCTURED_BLOCK_PATCH_OPS,
} from "../define-block-kind";
import { ATOM_BLOCK_DOC_NODE } from "../doc-node";

function instructionWith(template: string, detail: string): string {
	return detail ? `${template}\n\n${detail}` : template;
}

export const imageBlockContentSchema = z
	.object({
		altText: z.string(),
		/** `ContentAsset` id of the image; the host resolves it to a URL (P14). */
		assetId: z.string().min(1).optional(),
		caption: z.string().optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type ImageBlockContent = z.infer<typeof imageBlockContentSchema>;

export const imageBlockKind = defineBlockKind({
	type: "IMAGE",
	contentSchema: imageBlockContentSchema,
	defaultContent: { altText: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): ImageBlockContent =>
		compact({
			altText: str(raw.altText),
			assetId: optionalStr(raw.assetId),
			caption: optionalStr(raw.caption),
			...pickProvenance(raw),
		}),
	deriveNarrationText: (content) =>
		instructionWith(
			TOPIC_BLOCK_INSTRUCTION_TEMPLATES.IMAGE,
			str(content.altText) || str(content.caption),
		),
	estimateDwellMs: (content) =>
		estimateTextMs(str(content.caption)) + BLOCK_INTERACTION_HOLD_MS.IMAGE,
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export const videoBlockContentSchema = z
	.object({
		altText: z.string().optional(),
		caption: z.string().optional(),
		title: z.string().optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type VideoBlockContent = z.infer<typeof videoBlockContentSchema>;

export const videoBlockKind = defineBlockKind({
	type: "VIDEO",
	contentSchema: videoBlockContentSchema,
	defaultContent: {},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): VideoBlockContent =>
		compact({
			altText: optionalStr(raw.altText),
			caption: optionalStr(raw.caption),
			title: optionalStr(raw.title),
			...pickProvenance(raw),
		}),
	deriveNarrationText: (content) =>
		instructionWith(
			TOPIC_BLOCK_INSTRUCTION_TEMPLATES.VIDEO,
			str(content.caption) || str(content.title),
		),
	estimateDwellMs: (content, context) =>
		estimateTextMs(joinParts([str(content.title), str(content.caption)])) +
		(typeof context.knownMediaDurationMs === "number" &&
		context.knownMediaDurationMs > 0
			? context.knownMediaDurationMs
			: BLOCK_INTERACTION_HOLD_MS.VIDEO_UNKNOWN_LENGTH),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "poster_and_link" },
});

export const codeBlockContentSchema = z
	.object({
		code: z.string(),
		language: z.string(),
		caption: z.string().optional(),
		source: z.string().optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type CodeBlockContent = z.infer<typeof codeBlockContentSchema>;

export const codeBlockKind = defineBlockKind({
	type: "CODE",
	contentSchema: codeBlockContentSchema,
	defaultContent: { code: "", language: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): CodeBlockContent =>
		compact({
			code: str(raw.code),
			language: str(raw.language),
			caption: optionalStr(raw.caption),
			source: optionalStr(raw.source),
			...pickProvenance(raw),
		}),
	deriveNarrationText: (content) => str(content.code),
	estimateDwellMs: (content) =>
		estimateTextMs(joinParts([str(content.code), str(content.caption)])),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export const statisticBlockContentSchema = z
	.object({
		statisticValue: z.string(),
		statisticLabel: z.string(),
		source: z.string().optional(),
		caption: z.string().optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type StatisticBlockContent = z.infer<typeof statisticBlockContentSchema>;

export const statisticBlockKind = defineBlockKind({
	type: "STATISTIC",
	contentSchema: statisticBlockContentSchema,
	defaultContent: { statisticValue: "", statisticLabel: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): StatisticBlockContent =>
		compact({
			statisticValue: str(raw.statisticValue),
			statisticLabel: str(raw.statisticLabel),
			source: optionalStr(raw.source),
			caption: optionalStr(raw.caption),
			...pickProvenance(raw),
		}),
	deriveNarrationText: (content) =>
		joinParts([str(content.statisticValue), str(content.statisticLabel)]),
	estimateDwellMs: (content) =>
		estimateTextMs(
			joinParts([
				str(content.statisticValue),
				str(content.statisticLabel),
				str(content.caption),
			]),
		),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export const motionGraphicBlockContentSchema = z
	.object({
		/**
		 * Built-in frame-driven component (`@repo/remotion-compositions` block motion graphics) until
		 * the motion graphics library lands (P20); then `motionGraphicId` takes precedence.
		 */
		componentKey: z.string(),
		/** `MotionGraphicAsset` id (P20). */
		motionGraphicId: z.string().min(1).optional(),
		/** Component / asset parameters; validated by the component at render time. */
		params: z.record(z.string(), z.unknown()),
		/** What the graphic shows, for screen readers and narration. */
		altText: z.string(),
		caption: z.string().optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type MotionGraphicBlockContent = z.infer<
	typeof motionGraphicBlockContentSchema
>;

export const motionGraphicBlockKind = defineBlockKind({
	type: "MOTION_GRAPHIC",
	contentSchema: motionGraphicBlockContentSchema,
	defaultContent: { componentKey: "", params: {}, altText: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): MotionGraphicBlockContent =>
		compact({
			componentKey: str(raw.componentKey),
			motionGraphicId: optionalStr(raw.motionGraphicId),
			params: { ...asRecord(raw.params) },
			altText: str(raw.altText),
			caption: optionalStr(raw.caption),
			...pickProvenance(raw),
		}),
	deriveNarrationText: (content) =>
		str(content.caption) || str(content.altText),
	estimateDwellMs: (content) =>
		estimateTextMs(str(content.caption)) +
		BLOCK_INTERACTION_HOLD_MS.MOTION_GRAPHIC,
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "poster_and_link" },
});
